import { GAME_EVENTS } from '@kulee/tga-constant'
import BaseModule from './BaseModule'
import logger from './logger'
import type { Context, GameInitPayload } from './types'

class GameBase extends BaseModule {
  private loadCompleted = false

  constructor(context: Context) {
    super(context)

    // 游戏数据初始化
    this.context.pm.on(GAME_EVENTS.ON_GAME_INIT, async ({ data }: { data: GameInitPayload }) => {
      // 初始化广告
      try {
        await this.context.sdk.initAd()
      } catch (error) {
        logger.error('ad init error', error)
      }

      // 设置SDK游戏
      this.context.sdk.__setGame(data.game)
      this.context.sdk.onGameInit(data)
    })

    // 游戏开始
    this.context.pm.on(GAME_EVENTS.ON_GAME_START, () => {
      // 单机游戏先播放前置广告
      if (!this.context.sdk.__game.pvp) {
        // 前置广告
        this.context.sdk.showPrerollAd({
          onFinish: () => {
            // 开始游戏
            this.context.sdk.onGameStart()
          }
        })
        // this.context.sdk.onGameStart()
      } else {
        // 开始游戏
        this.context.sdk.onGameStart()
      }
    })

    // 暂停游戏
    this.context.pm.on(GAME_EVENTS.ON_GAME_PAUSE, () => {
      this.context.sdk.onGamePause()
    })

    // 继续游戏
    this.context.pm.on(GAME_EVENTS.ON_GAME_RESUME, () => {
      this.context.sdk.onGameResume()
    })
  }

  /**
   * 游戏上报加载进度，从开始加载到GAME_READY事件的发出
   * @param progress 游戏加载进度，取值范围0-1
   */
  public reportProgress(progress: number): void {
    logger.log('report progress', progress)

    if (progress > 1 || progress < 0) {
      logger.warn('game loading progress must between 0 to 1')
      return
    }

    this.pm.emit(GAME_EVENTS.REPORT_LOADING_PROGRESS, {
      progress
    })

    // 兼容旧游戏
    if (progress === 1) {
      this.gameLoadComplete()
    }
  }

  /**
   * 游戏资源加载完成
   */
  public gameLoadComplete(): void {
    if (this.loadCompleted) {
      return
    }

    this.loadCompleted = true
    logger.log('game load complete')

    this.pm.emit(GAME_EVENTS.GAME_LOAD_COMPLETE)
  }

  /**
   * 游戏准备就绪，随时可以开始
   */
  public gameReady(): void {
    logger.log('game ready')

    this.pm.emit(GAME_EVENTS.GAME_READY)
  }

  /**
   * 闯关类游戏必接，无限循环类游戏无需接
   * 上报用户通过的关卡
   * @param options 关卡信息
   */
  public reportLevelPassed(options: { level: number; levelTitle?: string }): void {
    logger.log('report level passed: ', options)

    this.pm.emit(GAME_EVENTS.REPORT_LEVEL_PASSED, options)
  }

  /**
   * 闯关类游戏必接，无限循环类游戏无需接
   * 上报用户游戏通关
   */
  public reportClearGame(): void {
    logger.log('report clear game')

    this.pm.emit(GAME_EVENTS.REPORT_CLEAR_GAME)
  }

  /**
   * 选接
   * 游戏分享
   */
  public share(): void {
    logger.log('share')

    this.pm.emit(GAME_EVENTS.SHARE)
  }

  /**
   * 游戏中如果有声音开关，必接
   * 上报游戏声音的状态
   * @param status 声音状态，有声：1/true，无声：0/false
   */
  public reportSoundStatus(status: 0 | 1 | boolean): void {
    logger.log('report sound status: ', status)
  }

  /**
   * 打开全局消息
   * @param options 全局消息信息
   */
  public openMessage(options: { type: 'success' | 'error' | 'warning'; content: string; onClosed: () => void }) {
    logger.log('open message: ', options)

    const { type, content, onClosed } = options
    this.pm.removeAll(GAME_EVENTS.ON_MESSAGE_CLOSED)
    this.pm.on(GAME_EVENTS.ON_MESSAGE_CLOSED, onClosed)

    this.pm.emit(GAME_EVENTS.OPEN_MESSAGE, {
      type,
      content
    })
  }

  public closeLoadingModal(): void {
    // 兼容旧游戏
    this.gameLoadComplete()
  }
}

export default GameBase
